import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { addWeeks } from 'date-fns/addWeeks'
import { isSameMonth } from 'date-fns/isSameMonth'
import { isSameYear } from 'date-fns/isSameYear'
import { subDays } from 'date-fns/subDays'
import { subMonths } from 'date-fns/subMonths'
import { subWeeks } from 'date-fns/subWeeks'

import { Maybe } from 'aem-libs-ts/src/types/utility'

import { DisableDatesFn } from '../types'

interface FocusDateTable {
  ArrowUp: () => Date
  ArrowDown: () => Date
  ArrowLeft: () => Date
  ArrowRight: () => Date
  Home: () => Date
  End: () => Date
  PageUp: () => Date
  PageDown: () => Date
  Enter: () => void
  Space: () => void
  Escape: () => void
}

const KeyAction = (
  focusedDate: Date,
  event: React.KeyboardEvent<HTMLTableElement>,
  shiftPress: boolean,
  onSelectDate: (date: Date) => void,
  onClose?: () => void
): FocusDateTable => {
  return {
    ArrowUp: () => {
      event?.preventDefault()
      return subWeeks(focusedDate, 1)
    },
    ArrowDown: () => {
      event?.preventDefault()
      return addWeeks(focusedDate, 1)
    },
    ArrowLeft: () => {
      event?.preventDefault()
      return subDays(focusedDate, 1)
    },
    ArrowRight: () => {
      event?.preventDefault()
      return addDays(focusedDate, 1)
    },
    Home: () => {
      event?.preventDefault()
      const day = focusedDate.getDay()
      return subDays(focusedDate, day === 0 ? 6 : day - 1)
    },
    End: () => {
      event?.preventDefault()
      const day = focusedDate.getDay()
      return addDays(focusedDate, day === 0 ? 0 : 7 - day)
    },
    PageUp: () => {
      event?.preventDefault()
      if (shiftPress) {
        return subMonths(focusedDate, 12)
      }

      if (focusedDate.getMonth() === 0) {
        return new Date(focusedDate.getFullYear() - 1, 11, focusedDate.getDate())
      }
      return subMonths(focusedDate, 1)
    },
    PageDown: () => {
      event?.preventDefault()
      if (shiftPress) {
        return addMonths(focusedDate, 12)
      }
      if (focusedDate.getMonth() === 11) {
        return new Date(focusedDate.getFullYear() + 1, 0, focusedDate.getDate())
      }
      return addMonths(focusedDate, 1)
    },
    Enter: () => {
      event?.preventDefault()
      onSelectDate?.(focusedDate)
    },
    Space: () => {
      event?.preventDefault()
      onSelectDate?.(focusedDate)
    },
    Escape: () => {
      event?.preventDefault()
      onClose?.()
    }
  }
}

export const useFocusDateTable = (
  date: Date,
  changeMounthYear: (month: number, year: number, day: number) => void,
  focusedDate: Date,
  setFocusedDate: (date: Date) => void,
  disabledDates: Maybe<DisableDatesFn>,
  onSelectDate: (date: Date) => void,
  onClose?: () => void
) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableElement>) => {
    const keyActions = KeyAction(focusedDate, event, event.shiftKey, onSelectDate, onClose)
    const newDateFocus = keyActions[event.code as keyof FocusDateTable]?.()

    if (newDateFocus && !disabledDates?.(newDateFocus)) {
      if (!isSameMonth(newDateFocus, date) || !isSameYear(newDateFocus, date)) {
        changeMounthYear(newDateFocus.getMonth(), newDateFocus.getFullYear(), newDateFocus.getDate())
      }
      setFocusedDate(newDateFocus)
    }
  }

  return { handleKeyDown, focusedDate }
}
